import { makeAutoObservable, runInAction } from "mobx";
import { getNullableChangesOrModel } from "../../../models/model-types-methods";
import BillingService from "../../../services/api/billing-service";
import { rootContainer, TYPE } from "../../../services/container/root";
import toastMessageListStore from "../../../components/toast-messages-modal/toast-messages-store";

export interface ISbrReceipt {
    id: number;
    date_from: string;
    date_to: string;
    company_tax_id?: string | null;
    other?: string | null;
    receipt_data?: string | null;
}

export interface ISbrReceiptChanges extends Partial<Omit<ISbrReceipt, "id">> {}

class SbrReceiptStore {
    private _model?: ISbrReceipt;
    private _changes: ISbrReceiptChanges;
    private _isGeneratingInvoice: boolean = false;

    private _service: BillingService = rootContainer.get<BillingService>(TYPE.BillingService);

    constructor(model?: ISbrReceipt) {
        makeAutoObservable(this, {}, { autoBind: true });
        this._changes = {} as ISbrReceiptChanges;
        if (model) {
            this.setModel(model);
        }
    }

    public get service(): BillingService {
        return this._service;
    }

    public get isGeneratingInvoice(): boolean {
        return this._isGeneratingInvoice;
    }

    public get dateFrom(): string | undefined {
        return this._changes.date_from ?? this._model?.date_from;
    }

    public get dateTo(): string | undefined {
        return this._changes.date_to ?? this._model?.date_to;
    }

    public get dateFromDateString(): string | undefined {
        return this._dateTimeToDateString(this.dateFrom);        
    }

    public get dateToDateString(): string | undefined {
        return this._dateTimeToDateString(this.dateTo);
    }

    public get companyTaxId(): string | null | undefined {
        return getNullableChangesOrModel(this._changes.company_tax_id, this._model?.company_tax_id);
    }

    public get other(): string | null | undefined {
        return getNullableChangesOrModel(this._changes.other, this._model?.other);
    }

    public get receiptData(): string | null | undefined {
        return getNullableChangesOrModel(this._changes.receipt_data, this._model?.receipt_data);
    }

    public setDateFrom(value: string | undefined) {
        this._changes.date_from = value;
    }

    public setDateTo(value: string | undefined) {
        this._changes.date_to = value;
    }

    public setCompanyTaxId(value: string | undefined) {
        this._changes.company_tax_id = value;
    }

    public setOther(value: string | undefined) {
        this._changes.other = value;
    } 

    public setModel(model: ISbrReceipt) {
        this._model = model;
    }

    public getUpdatedModel(): ISbrReceipt {
        return { ...this._model, ...this._changes } as ISbrReceipt;
    }

    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length;
    }

    public async calculateAndGenerateReceipt(callback: () => void | undefined) {
        try {
            this._isGeneratingInvoice = true;
            const updatedModel = this.getUpdatedModel();
            const response = await this.service.generateSbrReceipt(updatedModel);
            runInAction(() => {
                if (response.encoded_pdf) {
                    const fileName = `sbr-receipt_${this.dateFromDateString}_${this.dateFromDateString}.pdf`;
                    downloadPdfFromBase64String(response.encoded_pdf, fileName);
                    toastMessageListStore.add({
                        title: 'Receipt configured',
                        description: 'Receipt was downloaded to your browser.',
                    });
                }
                this._isGeneratingInvoice = false;
            });

            /** Call callback if provided */
            if (callback) {
                callback();
            }
        } catch (error) {
            runInAction(() => {
                this._isGeneratingInvoice = false;
            });
        }
    }

    private _dateTimeToDateString(dateTime: string | undefined): string | undefined {
        if (!dateTime) {
            return undefined;
        }
        const date = new Date(dateTime);
        return date.toISOString().split('T')[0];
    }
}

export default SbrReceiptStore;

const downloadPdfFromBase64String = (base64Url: string, fileName: string) => {
    if (!base64Url || !fileName) {
        console.error('Invalid base64Url or fileName');
        return;
    }
    
    const base64Prefix = 'data:application/pdf;base64,';
    if (!base64Url.startsWith(base64Prefix)) {
        base64Url = `${base64Prefix}${base64Url}`;
    }
    const link = document.createElement("a");
    link.href = base64Url;
    link.download = fileName;
    link.click();
};